import { useCallback, useEffect, useState, ReactNode } from 'react';
import { fetchTopics } from '../../api/fetch-topics';
import {
  IMessage,
  INlpResponse,
  ITopic,
  ITopicGroup,
  ITopicTag,
  TopicStatus,
} from '../../common/types';
import { AGREEMENT_BANNER_STORAGE_KEY } from '../../constants/local-storage';
import useLocalStorage from '../../hooks/use-local-storage';
import useRealPersonLiveChat from '../../hooks/use-realperson-live-chat';
import { CURRENT_INSTANCE } from '../../tenant';
import ChatProviderContext from '../contexts/chat-context';

interface IChatProviderProps {
  children: ReactNode;
}

function ChatProvider({ children, ...props }: IChatProviderProps) {
  const [topics, setTopics] = useState<ITopic[]>([]);
  const [topicGroups, setTopicGroups] = useState<ITopicGroup[]>([]);
  const [topicTags, setTopicTags] = useState<ITopicTag[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<string>('');
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [responses, setResponses] = useState<Array<{ id: number; response: INlpResponse }>>([]);
  const [showAgreementBanner, setShowAgreementBanner] = useState<boolean>(false);
  const [agreedToDisclaimer, setAgreedToDisclaimer] = useLocalStorage<boolean>(
    AGREEMENT_BANNER_STORAGE_KEY,
    false
  );
  const [agreedToDisclaimerPerSession, setAgreedToDisclaimerPerSession] = useState<boolean>(false);
  const addMessage = useCallback(
    (message: IMessage) => setMessages((prev) => [...prev, message]),
    []
  );
  const liveChat = useRealPersonLiveChat({
    messages,
    addMessage,
  });

  useEffect(() => {
    if (topics.length === 0) {
      fetchTopics()
        .then((result) => {
          if (result) {
            const activeOrDisabledTopics = result.filter(
              (topic) => topic.status !== TopicStatus.Inactive
            );

            if (CURRENT_INSTANCE.topics.length === 0) {
              setTopics(activeOrDisabledTopics);
              setTopicGroups(getUniqueTopicGroups(activeOrDisabledTopics));
              setTopicTags(getUniqueTopicTags(activeOrDisabledTopics));
            } else {
              const allowedTopics = activeOrDisabledTopics.filter((topic) =>
                CURRENT_INSTANCE.topics.includes(topic.name.toLowerCase())
              );
              setTopics(allowedTopics);
              setTopicGroups(getUniqueTopicGroups(allowedTopics));
              setTopicTags(getUniqueTopicTags(allowedTopics));
            }
          }
        })
        .catch(console.error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    topics,
    topicGroups,
    topicTags,
    selectedTopic,
    setSelectedTopic,
    messages,
    addMessage,
    responses,
    addResponse: (id: number, response: INlpResponse) =>
      setResponses((prev) => [...prev, { id, response }]),
    resetChat: () => {
      if (liveChat.isActive) {
        void liveChat.end();
      }
      setMessages([]);
      setResponses([]);
      setSelectedTopic('');
    },
    agreedToDisclaimer,
    setAgreedToDisclaimer,
    agreedToDisclaimerPerSession,
    setAgreedToDisclaimerPerSession,
    showAgreementBanner,
    setShowAgreementBanner,
    liveChat,
  };

  return (
    <ChatProviderContext.Provider {...props} value={value}>
      {children}
    </ChatProviderContext.Provider>
  );
}

function getUniqueTopicGroups(topics: ITopic[]): ITopicGroup[] {
  const allTopicGroups: ITopicGroup[] = topics
    .filter((topic) => topic.groupId)
    .map((topic) => ({
      id: topic.groupId!,
      name: topic.groupName!,
      synonyms: topic.groupSynonyms,
    }));

  return Array.from(new Map(allTopicGroups.map((item) => [item.id, item])).values());
}

function getUniqueTopicTags(topics: ITopic[]): ITopicTag[] {
  return Array.from(
    new Map(topics.flatMap((topic) => topic.tags).map((tag) => [tag.id, tag])).values()
  );
}

export default ChatProvider;
